import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Th, Td } from "@/components/common";
import { api, errMsg, cur } from "@/lib/api";

const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

function GlobalForm({ cfg, isAdmin, onSaved }) {
  const [f, setF] = useState({});
  useEffect(() => {
    setF({ tolerance_points: cfg.tolerance_points, reversal_stop_points: cfg.reversal_stop_points, reversal_hold_points: cfg.reversal_hold_points, starting_balance: cfg.starting_balance, skip_outside_range: cfg.skip_outside_range, auto_size: cfg.auto_size, eod_flat: cfg.eod_flat, windows: cfg.windows });
  }, [cfg]);
  const save = async () => {
    const body = { ...f, tolerance_points: num(f.tolerance_points), reversal_stop_points: num(f.reversal_stop_points), reversal_hold_points: num(f.reversal_hold_points), starting_balance: num(f.starting_balance) };
    if (!isAdmin) { delete body.windows; delete body.eod_flat; }
    try { await api.put("/levels/config", body); toast.success("Settings saved"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  const field = (k, label) => (
    <label className="space-y-1 text-xs text-slate-400" key={k}>{label}
      <Input data-testid={`levels-cfg-${k}`} value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="num h-9 bg-slate-950" />
    </label>
  );
  const setWin = (w, k, v) => setF({ ...f, windows: { ...f.windows, [w]: { ...f.windows[w], [k]: v } } });
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {field("tolerance_points", "Tolerance (pts)")}{field("reversal_stop_points", "Reversal stop (pts)")}{field("reversal_hold_points", "Reversal hold (pts)")}{field("starting_balance", `Starting balance (${cur()})`)}
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-300"><Switch data-testid="levels-cfg-skip-outside-range" checked={!!f.skip_outside_range} onCheckedChange={(v) => setF({ ...f, skip_outside_range: v })} />Skip gap days (price outside R2 or S2)</label>
      <label className="flex items-center gap-2 text-sm text-slate-300"><Switch data-testid="levels-cfg-auto-size" checked={!!f.auto_size} onCheckedChange={(v) => setF({ ...f, auto_size: v })} />Size trades from account balance (uses each market's size per {cur()}10k; paper balance, or the MT5 balance when armed)</label>
      {isAdmin && f.windows && (
        <div className="space-y-2">
          <div className="text-xs uppercase tracking-wider text-slate-500">Session windows (London) and EOD flat</div>
          <div className="grid gap-2 md:grid-cols-2">
            {Object.entries(f.windows).map(([w, v]) => (
              <div key={w} className="flex items-center gap-2 rounded border border-slate-800 p-2">
                <span className="w-12 text-xs text-slate-400">{w}</span>
                {["open", "cutoff", "close"].map((k) => <Input key={k} data-testid={`levels-window-${w}-${k}`} value={v[k]} onChange={(e) => setWin(w, k, e.target.value)} className="num h-8 bg-slate-950 text-xs" />)}
              </div>
            ))}
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-400">EOD flat at<Input data-testid="levels-cfg-eod-flat" value={f.eod_flat || ""} onChange={(e) => setF({ ...f, eod_flat: e.target.value })} className="num h-8 w-24 bg-slate-950" /></label>
        </div>
      )}
      <Button data-testid="levels-cfg-save" onClick={save} className="btn-gold">Save global settings</Button>
    </div>
  );
}

const NUMS = ["size", "size_per_10k", "take_profit_points", "stop_loss_points", "spread_points", "max_target_distance_points", "min_pivot_distance_points"];
const BOOLS = ["enabled", "hold_past_close", "reverse_at_target", "live_mirror"];

function Lots({ lt, k }) {
  if (!lt) return <span className="text-xs text-slate-500">MT5 not linked</span>;
  if (lt.error) return <span data-testid={`market-${k}-mt5-lots`} className="block max-w-[160px] text-[10px] leading-tight text-rose-300" title={lt.error}>{lt.error}</span>;
  return (
    <div data-testid={`market-${k}-mt5-lots`} className="num text-xs" title={`1 lot = ${lt.per_lot_per_point}/pt (contract ${lt.contract_size}, ${lt.profit_ccy} at ${lt.fx})`}>
      <div className="text-slate-100">{lt.lots} lots</div>
      <div className="text-[10px] text-slate-500">= {lt.actual_per_point}/pt</div>
    </div>
  );
}

function MarketRow({ m, lt, isAdmin, onSaved }) {
  const [f, setF] = useState(m);
  useEffect(() => { setF(m); }, [m]);
  const save = async () => {
    const body = {};
    NUMS.forEach((k) => { body[k] = num(f[k]); });
    BOOLS.forEach((k) => { body[k] = f[k]; });
    body.symbol = f.symbol;
    if (!isAdmin) { delete body.live_mirror; delete body.symbol; }
    try { await api.put(`/levels/markets/${m.key}`, body); toast.success(`${m.name} saved`); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <tr className="border-b border-slate-800/60" data-testid={`market-settings-row-${m.key}`}>
      <Td className="font-medium">{m.name}</Td>
      <Td><Input disabled={!isAdmin} list="mt5-symbol-list" data-testid={`market-${m.key}-symbol`} value={f.symbol || ""} onChange={(e) => setF({ ...f, symbol: e.target.value })} className="num h-8 w-28 bg-slate-950 text-xs" /></Td>
      {NUMS.map((k) => (
        <Td key={k}><Input data-testid={`market-${m.key}-${k}`} value={f[k] ?? ""} placeholder="-" onChange={(e) => setF({ ...f, [k]: e.target.value })} className="num h-8 w-20 bg-slate-950 text-xs" /></Td>
      ))}
      <Td><Lots lt={lt} k={m.key} /></Td>
      {BOOLS.map((k) => (
        <Td key={k}><Switch disabled={!isAdmin && k === "live_mirror"} data-testid={`market-${m.key}-${k}`} checked={!!f[k]} onCheckedChange={(v) => setF({ ...f, [k]: v })} /></Td>
      ))}
      <Td><Button data-testid={`market-${m.key}-save`} size="sm" variant="secondary" className="h-8" onClick={save}>Save</Button></Td>
    </tr>
  );
}

export function SettingsDrawer({ open, onOpenChange, cfg, board, isAdmin, onSaved }) {
  const [syms, setSyms] = useState([]);
  useEffect(() => {
    if (open && !syms.length) { api.get("/broker/symbols").then((r) => setSyms(r.data)).catch(() => {}); }
  }, [open, syms.length]);
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto border-slate-800 bg-[#0B0F17] sm:max-w-[96vw] xl:max-w-[1400px]" data-testid="levels-settings-drawer">
        <SheetHeader><SheetTitle className="font-display text-slate-100">Levels settings</SheetTitle></SheetHeader>
        <datalist id="mt5-symbol-list" data-testid="mt5-symbol-list">{syms.map((s) => <option key={s} value={s} />)}</datalist>
        <div className="mt-6 space-y-8">
          {cfg && <GlobalForm cfg={cfg} isAdmin={isAdmin} onSaved={onSaved} />}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1300px]">
              <thead className="border-b border-slate-800"><tr><Th>Market</Th><Th>MT5 symbol</Th><Th>Size {cur()}/pt</Th><Th>Size per {cur()}10k</Th><Th>TP pts</Th><Th>SL pts</Th><Th>Spread</Th><Th>Max target</Th><Th>Min pivot</Th><Th>MT5 lots</Th><Th>Enabled</Th><Th>Hold past close</Th><Th>Reverse</Th><Th>Live mirror</Th><Th /></tr></thead>
              <tbody>{(board || []).map(({ market, mt5_lots }) => <MarketRow key={market.key} m={market} lt={mt5_lots} isAdmin={isAdmin} onSaved={onSaved} />)}</tbody>
            </table>
          </div>
          <p className="text-xs text-slate-500">Blank TP means the trade aims at the pivot target. Blank SL means the stop sits at the opposite side target. MT5 lots are worked out automatically from each symbol's contract size, its currency and the live exchange rate, so the live order matches the {cur()}/pt size. If the broker's smallest size is well above your stake the live order is not sent.</p>
          {(board || []).some(({ market }) => market.window_override) && (
            <p className="text-xs text-[#EBD7A0]" data-testid="levels-window-overrides-note">Market window overrides (from Master settings): {(board || []).filter(({ market }) => market.window_override).map(({ market, window: w }) => `${market.name} ${w.open}-${w.cutoff}, close ${w.close}`).join("; ")}. These replace the session window above for that market and are marked * on the board.</p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
